import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { catchError, of } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";

import { StudentCardComponent } from "src/app/shared/components/network-card/student-card.component";
import {
  SearchContainerComponent,
  SearchDataSource,
  SearchParams,
  SearchResult,
  SearchState,
} from "src/app/shared/components/search";

import { StudentProfile } from "src/app/api/models/student-profile";
import { StudentProfilePagedData } from "src/app/api/models/student-profile-paged-data";
import { StudentProfileService } from "src/app/api/services/student-profile.service";
import { GetStudentProfiles$Params } from "src/app/api/fn/student-profile/get-student-profiles";
import { SortType } from "src/app/api/models/sort-type";

import { FilterOptionsService } from "src/app/shared/services/filter-options.service";
import { AlertService } from "src/app/shared/services/alert.service";

@Component({
  selector: "app-company-students",
  providers: [SearchState],
  imports: [TranslateModule, SearchContainerComponent, StudentCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-search-container
      [config]="searchConfig()"
      [dataSource]="dataSource"
      [fields]="fields()"
      [trackByFn]="trackByStudentId"
      [showMatchFilter]="true"
    >
      <ng-template #itemTemplate let-student let-layout="layout">
        <app-student-card
          [data]="student"
          [showMatchPercentage]="true"
          [layout]="layout ?? 'portrait'"
        ></app-student-card>
      </ng-template>
    </app-search-container>
  `,
})
export class CompanyStudentsComponent {
  private readonly studentProfileService = inject(StudentProfileService);
  private readonly searchState = inject(SearchState);
  private readonly filterOptionsService = inject(FilterOptionsService);
  private readonly alertService = inject(AlertService);

  fields = computed<FormlyFieldConfig[]>(() => [
    this.filterOptionsService.searchTermField(),
    this.filterOptionsService.buildLocationField({ label: "Location" }),
    this.filterOptionsService.educationLevelsField(),
    this.filterOptionsService.lookingForField(),
    this.filterOptionsService.languageCodesField(),
    this.filterOptionsService.areaOfInterestsField(),
  ]);

  searchConfig = computed(() => ({
    searchOptions: this.searchState.searchOptions(),
    context: {
      queryParamOptions: [
        "Location",
        "MaximumDistance",
        "AreaOfInterests",
        "EducationLevels",
        "SearchTerm",
        "LookingFor",
        "LanguageCodes",
        "MinimumMatchPercentage",
      ],
    },
    filterDisplayNames: {
      SearchTerm: "Search",
      MaximumDistance: "Radius",
      AreaOfInterests: "Field of interest",
      EducationLevels: "Education level",
      LookingFor: "Looking for",
      LanguageCodes: "Candidate language",
    },
    excludeFromBadges: ["MinimumMatchPercentage"],
    valueFormatters: {
      MaximumDistance: (value: string | number | boolean) => `${value} km`,
      MinimumMatchPercentage: (value: string | number | boolean) => `${value}%`,
    },
    filterOptions: {
      LanguageCodes: this.filterOptionsService.languageFilterOptions(),
    },
  }));

  dataSource: SearchDataSource<StudentProfile> = {
    fetch: async (
      params: SearchParams
    ): Promise<SearchResult<StudentProfile>> => {
      const apiParams = {
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
      } as GetStudentProfiles$Params;

      // Map standard filter params
      if (params["SearchTerm"])
        apiParams.SearchTerm = params["SearchTerm"] as string;
      if (params["MaximumDistance"])
        apiParams.MaximumDistance = Number(params["MaximumDistance"]);
      if (params["MinimumMatchPercentage"])
        apiParams.MinimumMatchPercentage = Number(
          params["MinimumMatchPercentage"]
        );
      if (params["EducationSearchTerm"])
        apiParams.EducationSearchTerm = params["EducationSearchTerm"] as string;
      // Map the search-state's generic "name" sort to the BE-recognised
      // "fullName". Other sort keys pass through unchanged.
      if (params["sortField"]) {
        const sortField = params["sortField"] as string;
        apiParams.SortField = sortField === "name" ? "fullName" : sortField;
      }
      if (params["sortType"])
        apiParams.SortType = params["sortType"] as SortType;

      // Array params
      const aoi = params["AreaOfInterests"];
      if (Array.isArray(aoi) && aoi.length)
        apiParams.AreaOfInterests = aoi as string[];
      const edu = params["EducationLevels"];
      if (Array.isArray(edu) && edu.length)
        apiParams.EducationLevels = edu as string[];
      const lf = params["LookingFor"];
      if (Array.isArray(lf) && lf.length) apiParams.LookingFor = lf[0] as any;
      const langs = params["LanguageCodes"];
      if (Array.isArray(langs) && langs.length)
        apiParams.LanguageCodes = langs as string[];

      const result = await firstValueFrom(
        this.studentProfileService.getStudentProfiles(apiParams).pipe(
          catchError((err) => {
            this.alertService.errorWithReport(
              err,
              "companyStudents:getStudentProfiles",
              {
                pageNumber: apiParams.PageNumber,
                pageSize: apiParams.PageSize,
                sortField: apiParams.SortField,
                sortType: apiParams.SortType,
              }
            );
            return of({ data: [], totalItems: 0 } as StudentProfilePagedData);
          })
        )
      );

      return {
        data: result?.data || [],
        totalItems: result?.totalItems || 0,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      };
    },
  };

  readonly trackByStudentId = (
    _index: number,
    student: StudentProfile
  ): string => student.id || "";
}
